// ============================================================================
// RECEIPT MODAL — React mirror of renderReceiptPdf.js
//
// Renders the exact same receipt layout as the printed PDF, inside a modal.
// This ensures a consistent "mental model" for the user — a referral receipt
// always looks the same whether it's right after submission or 3 months later.
//
// Props:
//   model           — output of buildFullReceiptModel(); null while loading
//   isLoading       — show spinner
//   error           — error message string; show error state
//   onClose         — called when user closes the modal
//   workflowState   — current workflow/state doc (optional)
//   workflowEvents  — ordered array of workflowEvents (optional)
//
// The "Download PDF" button calls renderReceiptPdf(model) directly.
// No PHI logic lives here — this file only renders whatever is in the model.
// ============================================================================

import React, { useCallback, useState, useEffect } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  X, Download, Loader2, AlertTriangle,
  ArrowRight, FileText, ClipboardList, Shield,
  CheckCircle, CheckCircle2, RefreshCw, Activity,
  Calendar, PauseCircle, AlertCircle,
  TrendingUp, MessageSquare, UserCheck, Eye,
} from 'lucide-react';
import { renderReceiptPdf } from '../core/receipt/renderReceiptPdf.js';
import { navigateToAttachmentViewer } from './AttachmentPdfViewer.jsx';

// ── Outcome style map ─────────────────────────────────────────────────────────

const OUTCOME_STYLES = {
  routine: { text: 'text-emerald-400', label: 'ROUTINE' },
  urgent:  { text: 'text-red-400',     label: 'URGENT'  },
  not_yet: { text: 'text-amber-400',   label: 'NOT YET' },
};

// ── Pure helpers ──────────────────────────────────────────────────────────────

const fmtBytes = (n) => {
  if (n == null || n < 0) return '—';
  if (n < 1024)            return `${n} B`;
  if (n < 1024 * 1024)     return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (ts, opts = { dateStyle: 'long', timeStyle: 'short' }) => {
  if (!ts) return '—';
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-US', opts);
  } catch {
    return String(ts);
  }
};

// ── Workflow status config ────────────────────────────────────────────────────

const WF_STATUS = {
  NEW:               { label: 'New',               cls: 'bg-blue-500/20 text-blue-400'       },
  NEEDS_INFO:        { label: 'Needs Info',         cls: 'bg-amber-500/20 text-amber-400'     },
  READY_TO_SCHEDULE: { label: 'Ready to Schedule',  cls: 'bg-indigo-500/20 text-indigo-400'   },
  SCHEDULED:         { label: 'Scheduled',          cls: 'bg-teal-500/20 text-teal-400'       },
  SEEN:              { label: 'Seen',               cls: 'bg-violet-500/20 text-violet-400'   },
  CLOSED_LOOP:       { label: 'Closed Loop',        cls: 'bg-emerald-500/20 text-emerald-400' },
};

// ── Priority config ───────────────────────────────────────────────────────────

const PRIORITY_CFG = {
  normal: null, // not shown — it's the default
  high:   { label: 'High Priority',   cls: 'bg-orange-500/20 text-orange-400',  Icon: TrendingUp   },
  urgent: { label: 'Urgent Priority', cls: 'bg-red-500/20 text-red-400',        Icon: AlertCircle  },
};

// ── Event type config ─────────────────────────────────────────────────────────

const EVT_CFG = {
  WORKFLOW_CREATED:          { label: 'Referral Created',      Icon: FileText,      color: 'text-blue-400'    },
  STATUS_CHANGED:            { label: 'Status Changed',        Icon: ArrowRight,    color: 'text-indigo-400'  },
  ENTERED_OPS:               { label: 'Entered Operations',    Icon: ClipboardList, color: 'text-teal-400'    },
  TRIAGE_UPDATED:            { label: 'Triage Updated',        Icon: Shield,        color: 'text-violet-400'  },
  TRIAGE_EXCEPTION_RECORDED: { label: 'Triage Exception',      Icon: AlertTriangle, color: 'text-orange-400'  },
  REWORK_REQUEST_CREATED:    { label: 'Rework Requested',      Icon: RefreshCw,     color: 'text-amber-400'   },
  REWORK_RESOLVED:           { label: 'Rework Resolved',       Icon: CheckCircle2,  color: 'text-emerald-400' },
  SCHEDULED_SET:             { label: 'Appointment Scheduled', Icon: Calendar,      color: 'text-teal-400'    },
  SEEN_SET:                  { label: 'Patient Seen',          Icon: UserCheck,     color: 'text-violet-400'  },
  CLOSED_LOOP_SET:           { label: 'Closed Loop',           Icon: CheckCircle2,  color: 'text-emerald-400' },
  OWNER_ASSIGNED:            { label: 'Coordinator Assigned',  Icon: UserCheck,     color: 'text-slate-400'   },
  PRIORITY_CHANGED:          { label: 'Priority Changed',      Icon: TrendingUp,    color: 'text-orange-400'  },
};

const TRIAGE_ACTION_LABELS = {
  TRIAGE_COMPLETED:        'Triage Completed',
  TRIAGE_ON_HOLD:          'Placed On Hold',
  TRIAGE_OVERRIDE_APPLIED: 'Override Applied',
  TRIAGE_SAVED:            'Triage Saved',
};

const ROLE_LABELS = {
  gp_employee:        'Referring Provider',
  intake_coordinator: 'Intake Coordinator',
  triage_nurse:       'Triage Nurse',
  scheduler:          'Scheduler',
  specialist:         'Specialist',
  specialist_admin:   'Specialist Admin',
  tenant_admin:       'Admin',
  ops_manager:        'Ops Manager',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionHeader = ({ title, subtitle }) => (
  <div className="flex items-baseline gap-2 border-b border-slate-700 pb-1.5 mb-3">
    <span className="text-xs font-bold uppercase tracking-wider text-slate-200">{title}</span>
    {subtitle && <span className="text-xs text-slate-500">{subtitle}</span>}
  </div>
);

const FieldRow = ({ label, value }) => (
  <div className="flex py-0.5 text-sm">
    <span className="w-2/5 shrink-0 pr-3 text-slate-400">{label}</span>
    <span className="text-slate-200 font-medium">{value || '—'}</span>
  </div>
);

// ── NoteCard — used for ops team notes ────────────────────────────────────────

const NoteCard = ({ icon: Icon, iconColor, label, sublabel, children }) => (
  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 space-y-1.5">
    <div className="flex items-center gap-2">
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${iconColor}`} />
      <span className="text-xs font-semibold text-slate-300">{label}</span>
      {sublabel && <span className="text-xs text-slate-600 ml-auto">{sublabel}</span>}
    </div>
    {children}
  </div>
);

// ── Workflow section — full ops pipeline state visible to the GP ───────────────

// ── Resolve display name from usersMap ───────────────────────────────────────
const resolveActorName = (uid, usersMap) => {
  if (!uid || !usersMap) return null;
  const p = usersMap[uid];
  if (!p) return null;
  if (p.displayName) return p.displayName;
  if (p.name)        return p.name;
  if (p.firstName || p.lastName) return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
  return null;
};

const WorkflowSection = ({ workflowState, workflowEvents, usersMap = {} }) => {
  if (!workflowState && (!workflowEvents || workflowEvents.length === 0)) return null;

  const ws = workflowState ?? {};
  const status      = ws.status;
  const statusCfg   = status ? (WF_STATUS[status] ?? null) : null;
  const priority    = ws.priority;
  const priorityCfg = priority ? (PRIORITY_CFG[priority] ?? null) : null;
  const isOnHold    = ws.onHold === true;

  // Scheduling timestamps
  const scheduledAt = ws.scheduling?.scheduledAt;
  const seenAt      = ws.scheduling?.seenAt;
  const closedAt    = ws.closedLoop?.closedAt;

  // Closed loop details
  const isClosedLoop    = status === 'CLOSED_LOOP';
  const closedByUid     = ws.closedLoop?.closedByUid || ws.closedLoop?.closedBy || null;
  const closedLoopNote  = ws.closedLoop?.closedLoopNote || ws.closedLoop?.note || null;

  // Notes from ops team
  const triageNote     = ws.triageNote     || null;
  const overrideReason = ws.overrideReason || null;
  const overrideNote   = ws.overrideNote   || null;
  const onHoldReason   = ws.onHoldReason   || null;

  // Has any ops note to show
  const hasOpsNotes = triageNote || overrideReason || onHoldReason || scheduledAt || seenAt || closedAt;
  const hasTimeline = workflowEvents && workflowEvents.length > 0;

  return (
    <div>
      <SectionHeader title="Referral Status & Ops Updates" />

      {/* ── Current status row ── */}
      {statusCfg && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs text-slate-500">Current status:</span>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusCfg.cls}`}>
            {statusCfg.label}
          </span>
          {priorityCfg && (
            <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${priorityCfg.cls}`}>
              <priorityCfg.Icon className="w-3 h-3" />
              {priorityCfg.label}
            </span>
          )}
          {isOnHold && (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-400">
              <PauseCircle className="w-3 h-3" />
              On Hold
            </span>
          )}
        </div>
      )}

      {/* ── Pipeline milestones (scheduling/seen/closed timestamps) ── */}
      {(scheduledAt || seenAt || closedAt) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          {scheduledAt && (
            <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Calendar className="w-3 h-3 text-teal-400" />
                <span className="text-xs font-semibold text-teal-400">Scheduled</span>
              </div>
              <p className="text-xs text-slate-300">
                {fmtDate(scheduledAt, { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            </div>
          )}
          {seenAt && (
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <UserCheck className="w-3 h-3 text-violet-400" />
                <span className="text-xs font-semibold text-violet-400">Patient Seen</span>
              </div>
              <p className="text-xs text-slate-300">
                {fmtDate(seenAt, { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            </div>
          )}
          {closedAt && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                <span className="text-xs font-semibold text-emerald-400">Closed Loop</span>
              </div>
              <p className="text-xs text-slate-300">
                {fmtDate(closedAt, { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── On hold notice ── */}
      {isOnHold && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 mb-3">
          <div className="flex items-center gap-2 mb-1">
            <PauseCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-400">Referral is On Hold</span>
          </div>
          {onHoldReason && (
            <p className="text-sm text-slate-300 leading-relaxed">
              <span className="text-slate-500 font-medium">On-hold reason:</span>{' '}
              <span className="italic">"{onHoldReason}"</span>
            </p>
          )}
          {!onHoldReason && (
            <p className="text-xs text-slate-500">No reason recorded.</p>
          )}
        </div>
      )}

      {/* ── Override notice ── */}
      {overrideReason && (
        <NoteCard
          icon={Shield}
          iconColor="text-violet-400"
          label="Triage Override Applied"
        >
          <p className="text-sm text-slate-300 leading-relaxed">{overrideReason}</p>
          {overrideNote && (
            <p className="text-xs text-slate-400 italic mt-1">"{overrideNote}"</p>
          )}
        </NoteCard>
      )}

      {/* ── Triage note ── */}
      {triageNote && (
        <div className="mt-3">
          <NoteCard
            icon={MessageSquare}
            iconColor="text-violet-400"
            label="Triage Note"
            sublabel="Triage Nurse"
          >
            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">
              {triageNote}
            </p>
          </NoteCard>
        </div>
      )}

      {/* ── No ops notes placeholder ── */}
      {!hasOpsNotes && !hasTimeline && (
        <p className="text-sm text-slate-500 mb-4">
          No updates from the ops team yet.
        </p>
      )}

      {/* ── Event timeline ── */}
      {hasTimeline && (
        <div className={`${hasOpsNotes ? 'mt-4' : ''}`}>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Activity Timeline
          </p>

          <div className="relative">
            {/* Vertical connector line */}
            <div className="absolute left-[15px] top-0 bottom-0 w-px bg-slate-700/60" />
            <div className="space-y-1">
              {workflowEvents.map((evt, idx) => {
                const cfg  = EVT_CFG[evt.eventType] || { label: evt.eventType || 'Event', Icon: Activity, color: 'text-slate-400' };
                const Icon = cfg.Icon;
                const isLast = idx === workflowEvents.length - 1;

                // Contextual descriptor
                let descriptor = null;
                if (evt.eventType === 'STATUS_CHANGED' && evt.fromStatus && evt.toStatus) {
                  const fCfg = WF_STATUS[evt.fromStatus];
                  const tCfg = WF_STATUS[evt.toStatus];
                  descriptor = (
                    <span className="flex items-center gap-1 flex-wrap mt-1">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${fCfg?.cls ?? 'bg-slate-700 text-slate-400'}`}>
                        {fCfg?.label ?? evt.fromStatus}
                      </span>
                      <ArrowRight className="w-3 h-3 text-slate-600" />
                      <span className={`px-1.5 py-0.5 rounded text-xs ${tCfg?.cls ?? 'bg-slate-700 text-slate-400'}`}>
                        {tCfg?.label ?? evt.toStatus}
                      </span>
                    </span>
                  );
                } else if (evt.eventType === 'TRIAGE_UPDATED') {
                  const lines = [];
                  if (evt.triageAction) {
                    lines.push(
                      <span key="action" className="text-xs text-slate-400">
                        {TRIAGE_ACTION_LABELS[evt.triageAction] ?? evt.triageAction}
                      </span>
                    );
                  }
                  if (evt.priority && evt.priority !== 'normal') {
                    const pCfg = PRIORITY_CFG[evt.priority];
                    lines.push(
                      <span key="priority" className={`text-xs font-medium ${pCfg?.cls?.split(' ')[1] ?? 'text-slate-400'}`}>
                        Priority → {evt.priority}
                      </span>
                    );
                  }
                  if (evt.overrideReason) {
                    lines.push(
                      <span key="override" className="text-xs text-slate-400">
                        Override: {evt.overrideReason}
                      </span>
                    );
                  }
                  if (evt.onHoldReason) {
                    lines.push(
                      <span key="hold" className="text-xs text-amber-400/80">
                        Hold: {evt.onHoldReason}
                      </span>
                    );
                  }
                  if (evt.notePreview) {
                    lines.push(
                      <span key="note" className="text-xs text-slate-500 italic">
                        "{evt.notePreview}{evt.notePreview.length >= 120 ? '…' : ''}"
                      </span>
                    );
                  }
                  if (lines.length > 0) {
                    descriptor = <div className="flex flex-col gap-0.5 mt-1">{lines}</div>;
                  }
                } else if (evt.eventType === 'TRIAGE_EXCEPTION_RECORDED' && evt.triageExceptionType) {
                  descriptor = (
                    <span className="text-xs text-orange-400/80 mt-1 block">
                      {evt.triageExceptionType.replace(/_/g, ' ')}
                    </span>
                  );
                } else if (evt.eventType === 'ENTERED_OPS' && evt.source) {
                  descriptor = (
                    <span className="text-xs text-slate-400 mt-1 block">
                      via {evt.source.toLowerCase()}
                    </span>
                  );
                } else if (evt.eventType === 'PRIORITY_CHANGED' && evt.priority) {
                  const pCfg = PRIORITY_CFG[evt.priority];
                  descriptor = (
                    <span className={`text-xs font-medium mt-1 block ${pCfg?.cls?.split(' ')[1] ?? 'text-slate-400'}`}>
                      → {evt.priority}
                    </span>
                  );
                }

                return (
                  <div key={evt.id ?? idx} className="relative flex gap-4 pb-1">
                    {/* Icon dot */}
                    <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-slate-800 border border-slate-700/60 flex items-center justify-center">
                      <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                    </div>
                    {/* Card */}
                    <div className={`flex-1 min-w-0 bg-slate-800/40 rounded-xl px-3 py-2.5 ${isLast ? 'border border-slate-600/30' : ''}`}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium text-slate-200">{cfg.label}</span>
                        <span className="text-xs text-slate-600 flex-shrink-0">
                          {fmtDate(evt.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                        </span>
                      </div>
                      {descriptor}
                      {evt.actorRole && (
                        <p className="text-xs text-slate-500 mt-1">
                          {ROLE_LABELS[evt.actorRole] ?? evt.actorRole}
                          {(() => {
                            const name = resolveActorName(evt.actorUid, usersMap);
                            return name ? <span className="text-slate-400"> — {name}</span> : null;
                          })()}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Closed Loop completion block ── */}
      {isClosedLoop && (
        <div className="mt-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-5 py-4">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-400">Referral Complete — Closed Loop</p>
              {closedAt && (
                <p className="text-xs text-slate-500 mt-0.5">
                  {fmtDate(closedAt, { dateStyle: 'long', timeStyle: 'short' })}
                </p>
              )}
            </div>
          </div>
          {(() => {
            const closerName = resolveActorName(closedByUid, usersMap);
            return (
              <p className="text-xs text-slate-400 ml-9.5 mt-0.5">
                {closerName ? `Closed by ${closerName}` : 'Closed by specialist team'}
              </p>
            );
          })()}
          {closedLoopNote && (
            <div className="mt-3 ml-0 bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3 py-2.5">
              <p className="text-xs font-semibold text-emerald-400/80 mb-1 uppercase tracking-wide">Specialist Note</p>
              <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{closedLoopNote}</p>
            </div>
          )}
          {!closedLoopNote && (
            <p className="text-xs text-slate-600 mt-1 ml-9.5">No closing note recorded.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Attachment view button (navigates to internal PDF viewer route) ────────────

function AttachmentViewButton({ referralId, attachmentId }) {
  const [sessionError, setSessionError] = useState(null);
  const [opening, setOpening] = useState(false);
  return (
    <>
      <button
        disabled={opening}
        onClick={async () => {
          if (opening) return;
          setOpening(true);
          try {
            setSessionError(null);
            await navigateToAttachmentViewer({ referralId, attachmentId });
          } catch (err) {
            setSessionError(safeUserError(err) ?? 'Failed to open document');
            setOpening(false);
          }
        }}
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded
                   transition-colors ${opening
                     ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                     : 'bg-slate-700 text-slate-200 hover:bg-slate-600'}`}
      >
        <Eye className="w-3.5 h-3.5" />
        <span>{opening ? 'Opening…' : 'View'}</span>
      </button>
      {sessionError && (
        <span className="text-xs text-red-400 ml-2">{sessionError}</span>
      )}
    </>
  );
}

// ── Receipt content ────────────────────────────────────────────────────────────

const ReceiptContent = ({ model, workflowState = null, workflowEvents = [], usersMap = {}, viewLevel = 'FULL', activeFocusTarget = null }) => {
  const {
    meta, outcome, pathway, provider, submittedAt,
    patient, contact, insurance, criteria, attachments,
  } = model;

  const outcomeStyle = OUTCOME_STYLES[outcome.status] || { text: 'text-slate-300', label: outcome.label };
  const generatedStr = fmtDate(meta.generatedAt);
  const submittedStr = fmtDate(submittedAt);

  const hl = (target) => activeFocusTarget === target ? 'learning-focus-highlight-subtle' : '';

  useEffect(() => {
    if (!activeFocusTarget) return;
    const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  }, [activeFocusTarget]);

  return (
    <div className="space-y-6 text-sm">

      {/* ── Header + Outcome ── */}
      <div data-learning-target="receipt_header_outcome_area" className={`flex items-start justify-between border-b border-slate-700 pb-4 rounded ${hl('receipt_header_outcome_area')}`}>
        <div>
          <div className="text-sm font-bold uppercase tracking-widest text-white">ReferralGate</div>
          <div className="text-xs text-slate-500 mt-0.5">Clinical Referral Receipt — CONFIDENTIAL</div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-black tracking-wider ${outcomeStyle.text}`}>
            {outcome.label}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {outcome.completenessScore}% complete ·{' '}
            {outcome.wasOverridden ? 'Override applied' : 'Gate decision'}
          </div>
        </div>
      </div>

      {/* ── Section: Referral Summary ── */}
      <div data-learning-target="receipt_referral_summary_bar" className={`rounded ${hl('receipt_referral_summary_bar')}`}>
        <SectionHeader title="Referral Summary" />
        <div className="mb-2">
          <span className="text-xs text-slate-500">Referral ID</span>
          <div className="text-sm font-bold text-white font-mono tracking-wide break-all">{meta.referralId || '—'}</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-800/60 border border-slate-700/50 rounded-lg px-4 py-3">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Specialty</div>
            <div className="text-xs font-semibold text-white">{pathway.specialtyName}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Pack</div>
            <div className="text-xs font-semibold text-white">{pathway.packName} v{pathway.packVersion}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Generated</div>
            <div className="text-xs font-semibold text-white">{generatedStr}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Visit Type</div>
            <div className="text-xs font-semibold text-white">{pathway.visitType}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Reason for Escalation</div>
            <div className="text-xs font-semibold text-white">{pathway.reason}</div>
          </div>
        </div>
      </div>

      {/* ── Section 1: Patient (PHI) — hidden for MEDIUM view level ── */}
      {viewLevel === 'FULL' && (
      <div data-learning-target="receipt_patient_phi_section" className={`rounded ${hl('receipt_patient_phi_section')}`}>
        <SectionHeader title="Patient" subtitle="PHI" />
        {(patient || contact || insurance) ? (
          <>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-3">
              <p className="text-xs text-amber-300">
                ⚠ Contains Protected Health Information (PHI) — Handle per HIPAA / tenant policy. Access logged.
              </p>
            </div>
            {patient && (
              <>
                <FieldRow label="Name"          value={patient.fullName} />
                <FieldRow label="Date of Birth" value={patient.dob} />
                <FieldRow label="Sex"           value={patient.sex} />
                {(patient.identifiers || []).map((id, i) => (
                  <FieldRow key={i} label={id.type || 'Identifier'} value={id.value} />
                ))}
              </>
            )}
            {contact && (
              <>
                {contact.phone   && <FieldRow label="Phone"   value={contact.phone} />}
                {contact.email   && <FieldRow label="Email"   value={contact.email} />}
                {contact.address && <FieldRow label="Address" value={contact.address} />}
              </>
            )}
            {insurance && (
              <>
                {insurance.payerName   && <FieldRow label="Payer"     value={insurance.payerName} />}
                {insurance.memberId    && <FieldRow label="Member ID" value={insurance.memberId} />}
                {insurance.groupNumber && <FieldRow label="Group #"   value={insurance.groupNumber} />}
                {insurance.planType    && <FieldRow label="Plan Type" value={insurance.planType} />}
              </>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">
            Patient data unavailable — PHI was not loaded for this receipt.
          </p>
        )}
      </div>
      )}

      {/* ── Section: Criteria & Completeness Snapshot ── */}
      <div data-learning-target="receipt_criteria_snapshot_section" className={`rounded ${hl('receipt_criteria_snapshot_section')}`}>
        <SectionHeader title="Criteria & Completeness Snapshot" />

        <div className="bg-slate-800/50 border-l-2 border-slate-600 px-3 py-2 mb-4 text-xs text-slate-400">
          Clinical criteria for{' '}
          <strong className="text-slate-200">{pathway.specialtyName}</strong>
          {' '}·{' '}
          Pack: <strong className="text-slate-200">{pathway.packName} v{pathway.packVersion}</strong>
        </div>

        {outcome.wasOverridden && (
          <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg px-3 py-3 mb-4">
            <div className="text-sm font-bold text-violet-400 mb-1">Gate Override Applied</div>
            <div className="text-sm text-slate-300">Reason: {outcome.overrideReason}</div>
            {outcome.overrideNote && (
              <div className="text-xs text-slate-400 italic mt-1">"{outcome.overrideNote}"</div>
            )}
            <div className="text-xs text-slate-500 mt-2">
              This override is logged and auditable. Submitted as ROUTINE.
            </div>
          </div>
        )}

        <div className="text-xs font-semibold text-slate-300 mb-2">Required Items:</div>
        {criteria.rows.length > 0 ? (
          <div className="space-y-2 mb-4">
            {criteria.rows.map((r, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className={`mt-0.5 shrink-0 text-sm ${r.provided ? 'text-emerald-400' : 'text-red-400'}`}>
                  {r.provided ? '✓' : '✗'}
                </span>
                <div>
                  <span className="text-sm text-slate-200">{r.label}</span>
                  {r.provided && r.answerText && (
                    <div className="text-xs font-semibold text-slate-100 mt-0.5">{r.answerText}</div>
                  )}
                  {r.helpText && (
                    <div className="text-xs text-slate-500 italic">{r.helpText}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 mb-4">No required criteria for this pack.</p>
        )}

        {criteria.triggeredRedFlags.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-red-400 mb-2">Red Flags Triggered:</div>
            {criteria.triggeredRedFlags.map((label, i) => (
              <div key={i} className="flex items-center gap-2 py-0.5 text-sm text-red-400">
                <span>⚠</span>
                <span>{label}</span>
              </div>
            ))}
          </div>
        )}

        {!outcome.wasOverridden && criteria.missingFields.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-amber-400 mb-2">Missing Required Items:</div>
            {criteria.missingFields.map((label, i) => (
              <div key={i} className="py-0.5 text-sm text-amber-400">• {label}</div>
            ))}
          </div>
        )}
      </div>

      {/* ── Section 5: Attachments ── */}
      {viewLevel === 'FULL' && (
      <div data-learning-target="receipt_attachments_section" className={`rounded ${hl('receipt_attachments_section')}`}>
        <SectionHeader
          title="Attachments"
          subtitle={attachments.length > 0 ? 'PHI — manifest only; no file contents' : ''}
        />
        {attachments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2 pr-3">#</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2 pr-3">Type</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2 pr-3">Status</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2 pr-3">Size</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2 pr-3">Uploaded</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/80">
                {attachments.map((att, i) => (
                  <tr key={att.attachmentId || i}>
                    <td className="py-2 pr-3 text-slate-500">{i + 1}</td>
                    <td className="py-2 pr-3 text-slate-200">{att.typeLabel}</td>
                    <td className={`py-2 pr-3 font-semibold ${
                      att.status === 'READY'  ? 'text-emerald-400' :
                      att.status === 'FAILED' ? 'text-red-400'     :
                      'text-slate-400'
                    }`}>
                      {att.status}
                    </td>
                    <td className="py-2 pr-3 text-slate-400">{fmtBytes(att.sizeBytes)}</td>
                    <td className="py-2 pr-3 text-slate-400">
                      {att.uploadedAt
                        ? fmtDate(att.uploadedAt, { dateStyle: 'medium', timeStyle: 'short' })
                        : '—'}
                    </td>
                    <td className="py-2 text-right">
                      {att.status === 'READY' && att.attachmentId && meta.referralId && (
                        <AttachmentViewButton referralId={meta.referralId} attachmentId={att.attachmentId} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No attachments on this referral.</p>
        )}
      </div>
      )}

      {/* ── Section 6: Referral Status & Ops Updates ── */}
      <div data-learning-target="receipt_timeline_section" className={`rounded ${hl('receipt_timeline_section')}`}>
        <WorkflowSection
          workflowState={workflowState}
          workflowEvents={workflowEvents}
          usersMap={usersMap}
        />
      </div>

      {/* ── Footer ── */}
      <div className="border-t border-slate-700 pt-3 flex flex-col sm:flex-row justify-between gap-1 text-xs text-slate-500">
        <span>ReferralGate — {meta.tenantId} · PHI access logged · Referral {meta.referralId}</span>
        <span className="shrink-0">Generated {generatedStr}</span>
      </div>

    </div>
  );
};

// ── Main modal ────────────────────────────────────────────────────────────────

const ReceiptModal = ({ model, isLoading, error, onClose, workflowState = null, workflowEvents = [], usersMap = {}, viewLevel = 'FULL', activeFocusTarget = null }) => {

  const hl = (target) => activeFocusTarget === target ? 'learning-focus-highlight-subtle' : '';

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleDownloadPdf = async () => {
    if (!model) return;
    try {
      await renderReceiptPdf(model);
    } catch (err) {
      console.error('[ReceiptModal] PDF render failed:', err);
      alert('PDF generation failed: ' + (safeUserError(err)));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 flex items-start justify-center overflow-y-auto py-8 px-4"
      onClick={handleBackdrop}
    >
      <div className="surface-warm bg-slate-900 border border-slate-700/50 rounded-xl w-full max-w-3xl shadow-2xl">

        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-sm font-semibold text-white shrink-0">Referral Receipt</span>
            {model?.meta?.referralId && (
              <span className="text-xs text-slate-500 font-mono truncate">
                {model.meta.referralId}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            {model && viewLevel === 'FULL' && (
              <button
                data-learning-target="receipt_download_pdf_button"
                onClick={handleDownloadPdf}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm transition-colors ${hl('receipt_download_pdf_button')}`}
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">

          {/* Loading */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
              <p className="text-slate-400 text-sm">Loading referral receipt…</p>
            </div>
          )}

          {/* Error */}
          {!isLoading && error && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <AlertTriangle className="w-10 h-10 text-red-400" />
              <p className="text-white font-medium">Failed to load receipt</p>
              <p className="text-slate-400 text-sm text-center max-w-sm">{error}</p>
            </div>
          )}

          {/* Content */}
          {!isLoading && !error && model && (
            <ReceiptContent
              model={model}
              workflowState={workflowState}
              workflowEvents={workflowEvents}
              usersMap={usersMap}
              viewLevel={viewLevel}
              activeFocusTarget={activeFocusTarget}
            />
          )}

        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
